// src/Pages/conges/MesConges.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { API_URL } from '../Config';
import { Calendar, Clock, Filter, RefreshCw, Plus, Eye, Trash2, AlertCircle, CheckCircle, XCircle, Clock as ClockIcon } from 'lucide-react';

const MesConges = () => {
  const [conges, setConges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchMesConges();
  }, []);

  const fetchMesConges = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/mes-conges`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setConges(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des congés');
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette demande ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/conges/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.success('Demande supprimée avec succès');
      fetchMesConges();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
      console.error('Erreur:', error);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      'cree': { 
        label: 'En attente', 
        color: 'bg-yellow-50 text-yellow-700 border-yellow-100',
        icon: <ClockIcon className="w-3 h-3" />
      },
      'valide_par_validateur': { 
        label: 'Validé', 
        color: 'bg-blue-50 text-blue-700 border-blue-100',
        icon: <CheckCircle className="w-3 h-3" />
      },
      'refuse_par_validateur': { 
        label: 'Refusé', 
        color: 'bg-red-50 text-red-700 border-red-100',
        icon: <XCircle className="w-3 h-3" />
      },
      'confirme_par_chef': { 
        label: 'Confirmé', 
        color: 'bg-green-50 text-green-700 border-green-100',
        icon: <CheckCircle className="w-3 h-3" />
      },
      'refuse_par_chef': { 
        label: 'Refusé', 
        color: 'bg-red-50 text-red-700 border-red-100',
        icon: <XCircle className="w-3 h-3" />
      },
    };
    return configs[status] || { 
      label: status, 
      color: ' text-gray-700 border-gray-100',
      icon: <AlertCircle className="w-3 h-3" />
    };
  };

  const getTypeConfig = (type) => {
    return type === 'exceptionnel' 
      ? { label: 'Exceptionnel', color: 'bg-purple-50 text-purple-700 border-purple-100' }
      : { label: 'Normal', color: ' text-gray-700 border-gray-100' };
  };

  const filteredConges = conges.filter(conge => {
    if (filter === 'all') return true;
    if (filter === 'pending') return conge.status === 'cree';
    if (filter === 'validated') return conge.status === 'valide_par_validateur';
    if (filter === 'confirmed') return conge.status === 'confirme_par_chef';
    if (filter === 'refused') return conge.status.includes('refuse');
    return true;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd MMM yyyy', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  const getStats = () => {
    return {
      total: conges.length,
      pending: conges.filter(c => c.status === 'cree').length,
      validated: conges.filter(c => c.status === 'valide_par_validateur').length,
      confirmed: conges.filter(c => c.status === 'confirme_par_chef').length,
      refused: conges.filter(c => c.status.includes('refuse')).length
    };
  };

  const stats = getStats();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-3 text-gray-500 text-sm">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Mes congés</h1>
              <p className="text-gray-500 text-sm mt-1">Gérez vos demandes de congé</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchMesConges}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
                title="Actualiser"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <Link
                to="/conges/nouveau"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle demande
              </Link>
            </div>
          </div>

          {/* Statistiques */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-500 mt-1">Total</div>
            </div>
            <div className="bg-white border border-yellow-100 rounded-lg p-4 bg-yellow-50">
              <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
              <div className="text-sm text-yellow-600 mt-1">En attente</div>
            </div>
            <div className="bg-white border border-blue-100 rounded-lg p-4 bg-blue-50">
              <div className="text-2xl font-bold text-blue-700">{stats.validated}</div>
              <div className="text-sm text-blue-600 mt-1">Validés</div>
            </div>
            <div className="bg-white border border-green-100 rounded-lg p-4 bg-green-50">
              <div className="text-2xl font-bold text-green-700">{stats.confirmed}</div>
              <div className="text-sm text-green-600 mt-1">Confirmés</div>
            </div>
            <div className="bg-white border border-red-100 rounded-lg p-4 bg-red-50">
              <div className="text-2xl font-bold text-red-700">{stats.refused}</div>
              <div className="text-sm text-red-600 mt-1">Refusés</div>
            </div>
          </div>

          {/* Filtres */}
          <div className="mb-6">
            <div className="flex items-center text-sm text-gray-500 mb-3">
              <Filter className="w-4 h-4 mr-2" />
              Filtrer par statut
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'Tous', count: stats.total },
                { key: 'pending', label: 'En attente', count: stats.pending },
                { key: 'validated', label: 'Validés', count: stats.validated },
                { key: 'confirmed', label: 'Confirmés', count: stats.confirmed },
                { key: 'refused', label: 'Refusés', count: stats.refused }
              ].map((item) => (
                <button
                  key={item.key}
                  onClick={() => setFilter(item.key)}
                  className={`px-4 py-2 text-sm rounded-md transition-colors ${
                    filter === item.key 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-white border border-gray-300 text-gray-700 hover:'
                  }`}
                >
                  {item.label} ({item.count})
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Liste des congés */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {filteredConges.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Calendar className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {conges.length === 0 ? 'Aucune demande de congé' : 'Aucun résultat'}
              </h3>
              <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                {conges.length === 0 
                  ? 'Vous n\'avez pas encore soumis de demande de congé. Créez votre première demande pour commencer.' 
                  : 'Aucune demande ne correspond aux filtres sélectionnés.'}
              </p>
              {conges.length === 0 && (
                <Link
                  to="/conges/nouveau"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Créer une demande
                </Link>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Période
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Durée
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredConges.map((conge) => {
                      const statusConfig = getStatusConfig(conge.status);
                      const typeConfig = getTypeConfig(conge.type);
                      
                      return (
                        <tr key={conge.id} className="hover:">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {formatDate(conge.date_debut)} - {formatDate(conge.date_fin)}
                            </div>
                            <div className="text-xs text-gray-500">
                              Créé le {formatDate(conge.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900">
                                {conge.duree} jour{conge.duree > 1 ? 's' : ''}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs border ${typeConfig.color}`}>
                              {typeConfig.label}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs border ${statusConfig.color}`}>
                                {statusConfig.icon}
                                <span className="ml-1.5">{statusConfig.label}</span>
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <Link
                                to={`/conge/${conge.id}`}
                                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-900"
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Voir
                              </Link>
                              {conge.status === 'cree' && (
                                <button
                                  onClick={() => handleDelete(conge.id)}
                                  className="inline-flex items-center text-sm text-red-600 hover:text-red-900"
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Supprimer
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Footer */}
              <div className=" px-6 py-3 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-gray-600">
                    {filteredConges.length} demande{filteredConges.length !== 1 ? 's' : ''} trouvée{filteredConges.length !== 1 ? 's' : ''}
                  </div>
                  <div className="text-sm text-gray-600 mt-2 sm:mt-0">
                    Total jours : {conges.reduce((sum, c) => sum + (c.duree || 0), 0)}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Informations */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Procédure</h3>
            <ul className="space-y-2">
              <li className="flex items-start text-sm text-gray-600">
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-3"></div>
                <span>Les demandes sont modifiables uniquement en statut "En attente"</span>
              </li>
              <li className="flex items-start text-sm text-gray-600">
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-3"></div>
                <span>Délai de traitement moyen : 2 à 3 jours ouvrés</span>
              </li>
              <li className="flex items-start text-sm text-gray-600">
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-3"></div>
                <span>Contactez les RH pour les demandes urgentes</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Recommandations</h3>
            <ul className="space-y-2">
              <li className="flex items-start text-sm text-gray-600">
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-3"></div>
                <span>Soumettez vos demandes au moins 2 semaines à l'avance</span>
              </li>
              <li className="flex items-start text-sm text-gray-600">
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-3"></div>
                <span>Vérifiez toujours votre solde disponible avant de soumettre</span>
              </li>
              <li className="flex items-start text-sm text-gray-600">
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-3"></div>
                <span>Fournissez des justifications claires et détaillées</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MesConges;